import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { roleLabel } from '@/lib/format';
import { failureOf } from '@/lib/result';
import { cn } from '@/lib/utils';
import { api } from '../client';
import { OVERVIEW_KEY } from '../overview/use-overview';
import { button, fieldLabel, formError, input, segment } from '../shell/look';
import { Modal } from '../shell/modal';
import { useToast } from '../shell/toast';
import { SourceAccessPicker } from './source-access-picker';
import { USERS_KEY } from './use-users';
import {
  CHANNEL_LABELS,
  EMPTY_USER,
  ROLES,
  firstFieldError,
  toNewUser,
  toggleChannel,
  userFormSchema,
} from './user-form';
import type { UserForm, UserValues } from './user-form';

type Form = UseFormReturn<UserForm, unknown, UserValues>;

const mono = 'font-mono text-[13.5px]';

/** "Allow a user" (design L648–692). The API checks everything again; 400/409 come back as text. */
export function AllowUserModal({ onClose }: { onClose: () => void }): JSX.Element {
  const form: Form = useForm<UserForm, unknown, UserValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: EMPTY_USER,
  });
  const [refused, setRefused] = useState<string | undefined>(undefined);
  const toast = useToast();
  // The rules span fields (a channel needs its id), so after a failed submit
  // every change re-checks the whole form, not only the field that changed.
  useEffect(() => {
    const sub = form.watch(() => {
      setRefused(undefined);
      if (form.formState.isSubmitted) void form.trigger();
    });
    return () => sub.unsubscribe();
  }, [form]);
  const client = useQueryClient();

  const submit = form.handleSubmit(async (values) => {
    setRefused(undefined);
    const r = await api.users.add({ body: toNewUser(values) });
    if (r.status !== 201) return setRefused(failureOf(r).detail ?? `The API answered ${r.status}.`);
    toast(`${r.body.user.name} can now talk to the agent`);
    await Promise.all([
      client.invalidateQueries({ queryKey: USERS_KEY }),
      client.invalidateQueries({ queryKey: OVERVIEW_KEY }),
    ]);
    onClose();
  });
  const error = firstFieldError(form.formState.errors) ?? refused;

  return (
    <Modal eyebrow="ACCESS CONTROL" title="Allow a user" onClose={onClose}>
      <form
        noValidate
        className="flex flex-col gap-3.5 overflow-y-auto px-7 py-6"
        onSubmit={(event) => void submit(event)}
      >
        <Identity form={form} />
        <Choices form={form} />
        <Sources form={form} />
        {error !== undefined && (
          <div role="alert" className={formError}>
            {error}
          </div>
        )}
        <p className="text-mute m-0 text-xs leading-normal">
          Tip: unknown senders are logged with their numeric ID under Activity, so a new user can
          message the bot once and you can allow them from there.
        </p>
        <div className="flex justify-end gap-2.5 pt-1.5">
          <button type="button" onClick={onClose} className={button.outline}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(button.primary, 'px-[18px]')}
          >
            Allow user
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Who they are: a name, and how each door recognises them. */
function Identity({ form }: { form: Form }): JSX.Element {
  const { register } = form;
  return (
    <>
      <label className={fieldLabel}>
        Name
        <input {...register('name')} placeholder="Maria Petrova" className={input} />
      </label>
      <div className="grid grid-cols-1 gap-3 tab:grid-cols-2">
        <label className={fieldLabel}>
          Telegram username
          <input {...register('telegram')} placeholder="@handle" className={cn(input, mono)} />
        </label>
        <label className={fieldLabel}>
          Telegram user ID
          <input
            {...register('telegramId')}
            placeholder="123456789"
            inputMode="numeric"
            className={cn(input, mono)}
          />
        </label>
      </div>
      <label className={fieldLabel}>
        Email (for Web UI)
        <input
          {...register('email')}
          type="email"
          placeholder="name@company.com"
          className={cn(input, 'text-[13.5px]')}
        />
      </label>
    </>
  );
}

/** Which sources they may read. Hidden for an admin, who reads every source. */
function Sources({ form }: { form: Form }): JSX.Element | null {
  const [role, mode, ids] = form.watch(['role', 'sourceMode', 'sourceIds']);
  if (role === 'admin') return null;
  const touch = { shouldDirty: true };
  return (
    <SourceAccessPicker
      mode={mode}
      sourceIds={ids}
      onMode={(m) => form.setValue('sourceMode', m, touch)}
      onSourceIds={(next) => form.setValue('sourceIds', next, touch)}
    />
  );
}

/** Role (one of three) and channels (one or both), as the design's segmented pills. */
function Choices({ form }: { form: Form }): JSX.Element {
  const role = form.watch('role');
  const channels = form.watch('channels');
  const pill = (on: boolean): string =>
    cn(on ? segment.on : segment.off, 'flex-1 px-2 py-1.5 text-[12.5px]');
  const touch = { shouldDirty: true };
  return (
    <div className="grid grid-cols-1 gap-3 tab:grid-cols-2">
      <div className={fieldLabel}>
        Role
        <div className={cn(segment.group, 'bg-surf2')}>
          {ROLES.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={role === r}
              className={pill(role === r)}
              onClick={() => form.setValue('role', r, touch)}
            >
              {roleLabel(r)}
            </button>
          ))}
        </div>
      </div>
      <div className={fieldLabel}>
        Channels
        <div className={cn(segment.group, 'bg-surf2')}>
          {(['telegram', 'web'] as const).map((channel) => (
            <button
              key={channel}
              type="button"
              aria-pressed={channels.includes(channel)}
              className={pill(channels.includes(channel))}
              onClick={() => form.setValue('channels', toggleChannel(channels, channel), touch)}
            >
              {CHANNEL_LABELS[channel]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
